// admin/src/pages/Questions.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminFetch } from '../utils/adminFetch';
import { useAdminAuth } from '../auth/AdminAuthContext';
import QuestionCardPro from '../components/QuestionCardPro';
import QuestionEditModal from '../components/QuestionEditModal';
import ConfirmDialog from '../components/ConfirmDialog';
import TopNav from '../components/TopNav';

const PHASE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Phases', icon: '🌐' },
  { value: '1', label: 'Phase 1: Mindset', icon: '🧠' },
  { value: '2', label: 'Phase 2: Bucket Discovery', icon: '🎯' },
  { value: '3', label: 'Phase 3: Area Refinement', icon: '🔍' }
];

const PHASE_CONFIG = {
  1: { 
    icon: '🧠', 
    label: 'Phase 1: Mindset Discovery', 
    color: '#8b5cf6',
    bgColor: '#f5f3ff',
    description: 'Questions to understand student motivations and mindsets'
  },
  2: { 
    icon: '🎯', 
    label: 'Phase 2: Career Bucket Discovery', 
    color: '#3b82f6',
    bgColor: '#eff6ff',
    description: 'Questions to identify top career interest buckets'
  },
  3: { 
    icon: '🔍', 
    label: 'Phase 3: Area Refinement', 
    color: '#10b981',
    bgColor: '#ecfdf5',
    description: 'Detailed questions within specific career buckets'
  }
};

export default function Questions() {
  const { secret, isAuthed } = useAdminAuth();
  const tenantId = 'demo-university';
  
  console.log('🔍 Questions Component - Auth state:', { 
    isAuthed, 
    hasSecret: !!secret
  });
  
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Accordion state - all phases expanded by default
  const [expandedPhases, setExpandedPhases] = useState({ 1: true, 2: true, 3: true });
  
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // StrictMode double-mount guard
  const didFetchRef = useRef(false);

  // Load questions
  const loadQuestions = useCallback(async () => {
    console.log('🔍 loadQuestions called - Auth state:', { 
      isAuthed, 
      hasSecret: !!secret
    });

    if (!isAuthed || !secret) {
      setLoading(false);
      setError('Not authenticated');
      console.log('❌ Skipping load - not authenticated');
      return;
    }

    setLoading(true);
    setError('');
    try {
      console.log('🔍 Making adminFetch call to /api/questions/admin...');
      const data = await adminFetch(`/api/questions/admin?tenantId=${tenantId}`, { secret });
      
      console.log('✅ Questions loaded successfully:', data?.length || 0, 'questions');
      
      // Sort by phase, then order, then _id
      const sorted = (data || []).sort((a, b) => {
        if (a.phase !== b.phase) return (a.phase || 0) - (b.phase || 0);
        if (a.order !== b.order) return (a.order || 0) - (b.order || 0);
        return String(a._id).localeCompare(String(b._id));
      });
      
      setQuestions(sorted);
      setFilteredQuestions(sorted);
    } catch (err) {
      console.error('❌ Failed to load questions:', err);
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [secret, isAuthed, tenantId]);

  // Load questions on mount with StrictMode guard
  useEffect(() => {
    console.log('🔍 useEffect triggered - didFetchRef.current:', didFetchRef.current);
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    
    if (isAuthed && secret) {
      console.log('🔍 Calling loadQuestions from useEffect');
      loadQuestions();
    } else {
      console.log('❌ Not calling loadQuestions - auth check failed');
    }
  }, [isAuthed, secret, loadQuestions]);

  // Filter questions
  useEffect(() => {
    let result = [...questions];
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(q => 
        (q.text || '').toLowerCase().includes(term) ||
        (q._id || '').toLowerCase().includes(term) ||
        (q.options || []).some(opt => (opt.text || '').toLowerCase().includes(term))
      );
    }
    
    if (phaseFilter !== 'all') {
      result = result.filter(q => String(q.phase) === phaseFilter);
    }
    
    if (statusFilter === 'enabled') {
      result = result.filter(q => q.enabled !== false);
    } else if (statusFilter === 'disabled') {
      result = result.filter(q => q.enabled === false);
    }
    
    setFilteredQuestions(result);
  }, [questions, searchTerm, phaseFilter, statusFilter]);

  // Save question
  const handleSave = async (questionData) => {
    try {
      const isUpdate = !!questionData._id;
      const path = isUpdate 
        ? `/api/questions/admin/${questionData._id}?tenantId=${tenantId}`
        : `/api/questions/admin?tenantId=${tenantId}`;
      const method = isUpdate ? 'PUT' : 'POST';
      
      await adminFetch(path, {
        method,
        body: { ...questionData, tenantId },
        secret
      });
      
      await loadQuestions();
      setIsModalOpen(false);
      setEditingQuestion(null);
    } catch (err) {
      throw new Error(err.message || 'Failed to save question');
    }
  };

  // Delete question
  const handleDelete = async () => {
    if (!deletingQuestion?._id) return;
    
    try {
      await adminFetch(
        `/api/questions/admin/${deletingQuestion._id}?tenantId=${tenantId}`,
        { method: 'DELETE', secret }
      );
      
      await loadQuestions();
      setIsDeleteDialogOpen(false);
      setDeletingQuestion(null);
    } catch (err) {
      console.error('Delete failed:', err);
      alert(err.message || 'Failed to delete question');
    }
  };

  // Toggle phase expansion
  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  // Expand/collapse all phases
  const expandAll = () => setExpandedPhases({ 1: true, 2: true, 3: true });
  const collapseAll = () => setExpandedPhases({ 1: false, 2: false, 3: false });

  // Group questions by phase
  const questionsByPhase = {
    1: filteredQuestions.filter(q => q.phase === 1),
    2: filteredQuestions.filter(q => q.phase === 2),
    3: filteredQuestions.filter(q => q.phase === 3)
  };

  const stats = {
    total: questions.length,
    phase1: questions.filter(q => q.phase === 1).length,
    phase2: questions.filter(q => q.phase === 2).length,
    phase3: questions.filter(q => q.phase === 3).length,
    enabled: questions.filter(q => q.enabled !== false).length,
  };

  // Show auth message if not authenticated
  if (!isAuthed) {
    return (
      <div className="admin-container">
        <TopNav />
        <div className="adm-content">
          <div style={{ textAlign: 'center', padding: 48, background: '#fef2f2', borderRadius: 12, border: '2px solid #fca5a5' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔒</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>
              Authentication Required
            </div>
            <div style={{ color: '#991b1b' }}>
              Please log in to access the Questions page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <TopNav />
      
      <div className="adm-content">
        {/* Header */}
        <div className="adm-header">
          <div>
            <h1 className="adm-heading">📝 Questions</h1>
            <p className="adm-subheading">Manage quiz questions across all phases</p>
          </div>
          <button className="btn btn--primary" onClick={() => { setEditingQuestion(null); setIsModalOpen(true); }}>
            ➕ New Question
          </button>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 24,
          padding: 16,
          background: '#f9fafb',
          borderRadius: 12,
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Total Questions</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{stats.total}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>🧠 Phase 1</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#8b5cf6' }}>{stats.phase1}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>🎯 Phase 2</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{stats.phase2}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>🔍 Phase 3</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{stats.phase3}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Enabled</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{stats.enabled}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="adm-filters" style={{ marginBottom: 24 }}>
          <input
            type="search"
            className="input"
            placeholder="🔍 Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select className="input" value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} style={{ minWidth: 200 }}>
            {PHASE_FILTER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
            ))}
          </select>
          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 150 }}>
            <option value="all">All Status</option>
            <option value="enabled">✅ Enabled</option>
            <option value="disabled">❌ Disabled</option>
          </select>
        </div>

        {/* Expand/Collapse All Controls */}
        {!loading && filteredQuestions.length > 0 && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <button 
              className="btn btn--secondary" 
              onClick={expandAll}
              style={{ fontSize: '0.875rem', padding: '6px 12px' }}
            >
              ⬇️ Expand All
            </button>
            <button 
              className="btn btn--secondary" 
              onClick={collapseAll}
              style={{ fontSize: '0.875rem', padding: '6px 12px' }}
            >
              ⬆️ Collapse All
            </button>
          </div>
        )}

        {/* Error */}
        {error && <div className="error" role="alert" style={{ marginBottom: 24 }}>{error}</div>}

        {/* Loading */}
        {loading && <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading questions...</div>}

        {/* No Results */}
        {!loading && filteredQuestions.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, background: '#f9fafb', borderRadius: 12, border: '2px dashed #d1d5db' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>📝</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: 8 }}>
              {questions.length === 0 ? 'No questions yet' : 'No questions match your filters'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: 24 }}>
              {questions.length === 0 ? 'Create your first question to get started' : 'Try adjusting your search or filters'}
            </div>
            {questions.length === 0 && (
              <button className="btn btn--primary" onClick={() => { setEditingQuestion(null); setIsModalOpen(true); }}>
                ➕ Create First Question
              </button>
            )}
          </div>
        )}

        {/* Phase Accordions */}
        {!loading && filteredQuestions.length > 0 && (
          <>
            <div style={{ marginBottom: 12, fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>
              Showing {filteredQuestions.length} of {stats.total} questions
            </div>

            {[1, 2, 3].map(phase => {
              const phaseQuestions = questionsByPhase[phase];
              const config = PHASE_CONFIG[phase];
              
              if (phaseQuestions.length === 0) return null;

              return (
                <div key={phase} style={{ marginBottom: 20 }}>
                  {/* Phase Header */}
                  <div
                    onClick={() => togglePhase(phase)}
                    style={{
                      padding: 20,
                      background: config.bgColor,
                      borderRadius: 12,
                      cursor: 'pointer',
                      border: `2px solid ${config.color}20`,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = config.color;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = `${config.color}20`;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 12,
                        marginBottom: 8
                      }}>
                        <span style={{ fontSize: '1.75rem' }}>{config.icon}</span>
                        <h2 style={{ 
                          fontSize: '1.25rem', 
                          fontWeight: 600, 
                          color: config.color,
                          margin: 0
                        }}>
                          {config.label}
                        </h2>
                        <span style={{
                          background: config.color,
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}>
                          {phaseQuestions.length}
                        </span>
                      </div>
                      <p style={{ 
                        fontSize: '0.875rem', 
                        color: '#6b7280',
                        margin: 0
                      }}>
                        {config.description}
                      </p>
                    </div>
                    <div style={{ 
                      fontSize: '1.5rem',
                      color: config.color,
                      transition: 'transform 0.2s',
                      transform: expandedPhases[phase] ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </div>
                  </div>

                  {/* Phase Questions Grid */}
                  {expandedPhases[phase] && (
                    <div 
                      className="adm-grid" 
                      style={{ 
                        marginTop: 16,
                        paddingLeft: 8
                      }}
                    >
                      {phaseQuestions.map((question) => (
                        <QuestionCardPro
                          key={question._id}
                          question={question}
                          onEdit={() => { setEditingQuestion(question); setIsModalOpen(true); }}
                          onDelete={() => { setDeletingQuestion(question); setIsDeleteDialogOpen(true); }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <QuestionEditModal
        question={editingQuestion}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingQuestion(null); }}
        onSave={handleSave}
        tenantId={tenantId}
        adminSecret={secret}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Question?"
        message={
          <>
            Are you sure you want to delete this question?
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, fontSize: '0.875rem', color: '#991b1b' }}>
              <strong>"{deletingQuestion?.text}"</strong>
            </div>
            <div style={{ marginTop: 12, fontSize: '0.875rem', color: '#6b7280' }}>
              This will also delete all student responses to this question. This action cannot be undone.
            </div>
          </>
        }
        confirmLabel="Delete Question"
        onConfirm={handleDelete}
        onCancel={() => { setIsDeleteDialogOpen(false); setDeletingQuestion(null); }}
      />
    </div>
  );
}